import { useCallback, useEffect, useRef, useState, type FormEvent, type ChangeEvent } from "react";
import { Send, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useHydrated } from "@/hooks/useHydrated";

const sourceOptions = [
  { value: "", label: "Bitte wählen..." },
  { value: "presse", label: "Presse" },
  { value: "veranstaltung", label: "Veranstaltung / Messe" },
  { value: "online-suche", label: "Online Suche" },
  { value: "netzwerk", label: "Netzwerk" },
  { value: "social-media", label: "Social Media" },
  { value: "sonstiges", label: "Sonstiges" },
];

/**
 * Wiederverwendbares Kontaktformular (extrahiert aus der Kontaktseite).
 * Inkl. Honeypot, HMAC-signierter Sicherheitsfrage und Datenschutz-Checkbox.
 * Sendet an /api/public/verify-contact.
 */
export function ContactForm({
  id,
  heading = "Nachricht senden",
  onFormStart,
  onSubmitSuccess,
}: {
  id?: string;
  heading?: string;
  onFormStart?: () => void;
  onSubmitSuccess?: () => void;
}) {

  const [form, setForm] = useState({
    name: "",
    company: "",
    fleet: "",
    email: "",
    phone: "",
    message: "",
    source: "",
    privacy: false,
    newsletter: false,
    quiz: "",
    website: "", // honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [quizQuestion, setQuizQuestion] = useState("Wird geladen…");
  const [quizToken, setQuizToken] = useState("");
  const [quizLoading, setQuizLoading] = useState(false);
  const loadedAtRef = useRef<number>(Date.now());
  const formStartedRef = useRef(false);
  const hydrated = useHydrated();


  const fetchQuiz = useCallback(async () => {
    setQuizLoading(true);
    try {
      const res = await fetch("/api/public/verify-contact", { method: "GET" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { question: string; token: string };
      setQuizQuestion(data.question);
      setQuizToken(data.token);
      loadedAtRef.current = Date.now();
    } catch (e) {
      console.error("Quiz laden fehlgeschlagen:", e);
      setQuizQuestion("Sicherheitsfrage konnte nicht geladen werden.");
    } finally {
      setQuizLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchQuiz();
  }, [fetchQuiz]);

  function onChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) {
    const { name, value, type } = e.target;
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      onFormStart?.();
    }
    setForm((prev) => ({
      ...prev,

      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg("");
    if (form.website) return;
    if (!form.privacy) {
      const msg = "Bitte stimmen Sie der Datenverarbeitung zu.";
      setErrorMsg(msg);
      toast.error(msg);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/verify-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          company: form.company,
          fleet: form.fleet,
          email: form.email,
          phone: form.phone,
          message: form.message,
          source: form.source,
          privacy: form.privacy,
          newsletter: form.newsletter,
          quizAnswer: form.quiz,
          token: quizToken,
          website: form.website,
          loadedAt: loadedAtRef.current,
          sourcePage:
            typeof window !== "undefined"
              ? window.location.href
              : "smartlane.ai",
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; ok?: boolean };
      if (!res.ok || !data.ok) {
        const msg = data.error ?? "Beim Senden ist ein Fehler aufgetreten.";
        setErrorMsg(msg);
        toast.error(msg);
        await fetchQuiz();
        setForm((p) => ({ ...p, quiz: "" }));
        return;
      }
      setSubmitted(true);
      onSubmitSuccess?.();

      toast.success("Vielen Dank! Wir melden uns innerhalb eines Werktags.");
    } catch (err) {
      console.error("Submit error:", err);
      const msg =
        "Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt an info@smartlane.ai.";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div id={id}>
      <div className="rounded-3xl border border-border/30 bg-white p-8 md:p-10 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.12)]">
        {!hydrated ? (
          <div className="space-y-5">
            <h2 className="font-heading text-2xl uppercase tracking-wider text-rich-black mb-2">
              {heading}
            </h2>
            <div className="h-80 animate-pulse rounded-xl bg-muted" aria-hidden="true" />
          </div>
        ) : submitted ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-smartlane-orange/10">
              <Sparkles className="h-10 w-10 text-smartlane-orange" />
            </div>
            <h3 className="font-heading text-3xl uppercase tracking-wider text-rich-black mb-3">
              Vielen Dank!
            </h3>
            <p className="font-body text-dark-gray">
              Wir haben Ihre Nachricht erhalten und melden uns zeitnah
              bei Ihnen.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5" suppressHydrationWarning data-lpignore="true">
            {/* Honeypot */}
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: -10000,
                width: 1,
                height: 1,
                overflow: "hidden",
              }}
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                data-lpignore="true"
                value={form.website}
                onChange={onChange}
              />
            </div>

            <h2 className="font-heading text-2xl uppercase tracking-wider text-rich-black mb-2">
              {heading}
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Name *"
                name="name"
                value={form.name}
                onChange={onChange}
                required
                placeholder="Max Mustermann"
              />
              <Field
                label="Firmenname *"
                name="company"
                value={form.company}
                onChange={onChange}
                required
                placeholder="Muster GmbH"
              />
            </div>

            <Field
              label="Größe Fuhrpark *"
              name="fleet"
              value={form.fleet}
              onChange={onChange}
              required
              placeholder="z.B. 50 Fahrzeuge"
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="E-Mail *"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                placeholder="name@firma.de"
              />
              <Field
                label="Telefonnummer"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={onChange}
                placeholder="+49 ..."
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="mb-1.5 block font-body text-sm text-dark-gray"
              >
                Ihre Nachricht *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={4}
                data-lpignore="true"
                value={form.message}
                onChange={onChange}
                placeholder="Beschreiben Sie Ihr Anliegen"
                className="w-full resize-none rounded-xl border border-border/60 bg-white px-4 py-3 font-body text-sm text-rich-black placeholder:text-muted-foreground transition-all focus:border-smartlane-orange focus:outline-none focus:ring-2 focus:ring-smartlane-orange/20"
              />
            </div>

            <div>
              <label
                htmlFor="source"
                className="mb-1.5 block font-body text-sm text-dark-gray"
              >
                Wie sind Sie auf uns aufmerksam geworden?
              </label>
              <select
                id="source"
                name="source"
                data-lpignore="true"
                value={form.source}
                onChange={onChange}
                className="h-12 w-full appearance-none rounded-xl border border-border/60 bg-white px-4 font-body text-sm text-rich-black transition-all focus:border-smartlane-orange focus:outline-none focus:ring-2 focus:ring-smartlane-orange/20"
              >
                {sourceOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Sicherheitsfrage (HMAC-signiert, 15 Min gültig) */}
            <div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label
                  htmlFor="quiz"
                  className="block font-body text-sm text-dark-gray"
                >
                  Sicherheitsfrage * — {quizQuestion}
                </label>
                <button
                  type="button"
                  onClick={() => void fetchQuiz()}
                  disabled={quizLoading}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-dark-gray transition hover:text-smartlane-orange disabled:opacity-50"
                  aria-label="Neue Sicherheitsfrage laden"
                >
                  <RefreshCw className={`h-3 w-3 ${quizLoading ? "animate-spin" : ""}`} />
                  Neu
                </button>
              </div>
              <input
                id="quiz"
                name="quiz"
                type="text"
                inputMode="numeric"
                required
                data-lpignore="true"
                value={form.quiz}
                onChange={onChange}
                placeholder="Ihre Antwort"
                className="h-12 w-full rounded-xl border border-border/60 bg-white px-4 font-body text-sm text-rich-black placeholder:text-muted-foreground transition-all focus:border-smartlane-orange focus:outline-none focus:ring-2 focus:ring-smartlane-orange/20"
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="privacy"
                data-lpignore="true"
                checked={form.privacy}
                onChange={onChange}
                className="mt-1 h-5 w-5 rounded border-border/60 accent-smartlane-orange focus:ring-smartlane-orange/30"
              />
              <span className="font-body text-sm leading-relaxed text-dark-gray">
                * Ich stimme der Datenverarbeitung zu. Weitere
                Informationen finden Sie in der{" "}
                <a
                  href="/datenschutz"
                  className="text-smartlane-orange hover:underline"
                >
                  Datenschutzerklärung
                </a>
                .
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="newsletter"
                data-lpignore="true"
                checked={form.newsletter}
                onChange={onChange}
                className="mt-1 h-5 w-5 rounded border-border/60 accent-smartlane-orange focus:ring-smartlane-orange/30"
              />
              <span className="font-body text-sm leading-relaxed text-dark-gray">
                Ich möchte zusätzlich den Smartlane-Newsletter abonnieren.
              </span>
            </label>

            {errorMsg && (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 font-body text-sm text-red-700"
              >
                {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-smartlane-orange font-body text-sm font-semibold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.01] hover:bg-smartlane-orange-glow active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:scale-100"
            >
              <Send className="h-4 w-4" />
              {submitting ? "Wird gesendet…" : "Nachricht senden"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  value,
  onChange,
  required,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block font-body text-sm text-dark-gray"
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        data-lpignore="true"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="h-12 w-full rounded-xl border border-border/60 bg-white px-4 font-body text-sm text-rich-black placeholder:text-muted-foreground transition-all focus:border-smartlane-orange focus:outline-none focus:ring-2 focus:ring-smartlane-orange/20"
      />
    </div>
  );
}
